/**
 * De un precio a lo que pinta `SalePrice`, y a lo que se manda a analítica.
 *
 * Existe porque el paywall y las landings ya no responden a la misma pregunta.
 * Antes había una sola regla de rebaja —la env var global— y vivía dentro del
 * componente. Desde que el paywall pinta lo que va a cobrar el proveedor
 * (enmienda a la regla 5 de ADR-021), la rebaja del paywall la declara Dodo y la
 * de las landings sigue siendo editorial. Son dos políticas, así que salen del
 * componente y él se queda con lo suyo: maquetar.
 */

import { getDiscountedPrice, isWebDiscountActive } from '@/lib/pricing'
import type { CurrencyCode } from '@/server/domain/money'
import { getPrice, TRIAL_PACKAGE_ID, type PricedPackageId, type PriceCurrency } from '@/server/domain/pricing'
import type { PaywallPricingPolicies, QuotedPricesByPackage, QuotedPrice } from '@/server/domain/quoted-price'

/**
 * Lo que hay que pintar. **Unión discriminada, no tres campos anulables**: con
 * `originalPrice` y `discountLabel` opcionales por separado, "hay rebaja" se
 * decidía dos veces —una en el componente al ramificar sobre `originalPrice`, y
 * otra al calcular el porcentaje— y las dos podían discrepar.
 *
 * No era teórico: por el sesgo de ±1 unidad mínima que Dodo introduce al
 * convertir divisa, `og_price: 500` con `total_price: 499` pintaba un badge de
 * «-0%», y `og_price: 946` con `total_price: 947` pintaba el tachado AL REVÉS
 * (946 tachado junto a 947 destacado). Con la unión, "tachado sin badge" deja de
 * ser un estado expresable.
 */
export type PriceDisplay =
  | { kind: 'plain'; price: string }
  | { kind: 'sale'; price: string; originalPrice: string; discountLabel: string }

/**
 * Precio del paywall ya resuelto: de dónde salió deja de importar salvo para
 * elegir cómo se formatea.
 *
 * Se resuelve **una vez** y de ahí salen tanto lo que se pinta como lo que se
 * manda a analítica. Antes eran dos funciones con la misma decisión dentro, y ya
 * discrepaban: en el camino degradado el usuario veía el precio con descuento y
 * `package_selected` reportaba el de catálogo sin descuento.
 *
 * Unión discriminada por `source` y no un registro con `currency: string`: la
 * regla 3 de ADR-021 mantiene `CurrencyCode` y `PriceCurrency` separados **a
 * propósito**, y colapsarlos aquí borraba justo la marca que impide confundir
 * EUR con MXN — en la capa donde esa confusión ES el bug que este trabajo
 * arregla. De paso, el discriminante deja el símbolo del escaparate indexable
 * sin cast ni fallback.
 */
export type ResolvedPaywallPrice =
  | {
      source: 'provider'
      /** Precio cotizado; Stripe puede convertirlo al pagar (ADR-042). */
      amount: number
      /** Cualquier divisa ISO-4217: el proveedor cobra en la del comprador. */
      currency: CurrencyCode
      /** Precio antes de la rebaja, o `null` si no hay ninguna declarada. */
      originalAmount: number | null
    }
  | {
      source: 'catalog'
      /** Lo que anunciamos. */
      amount: number
      /** Sólo las dos del escaparate: el catálogo no conoce más (regla 3). */
      currency: PriceCurrency
      originalAmount: number | null
    }

/**
 * El precio del proveedor si se pudo preguntar, el del catálogo si no.
 *
 * Ésta es la **degradación** que hace que la enmienda sea segura de desplegar:
 * ninguna caída del proveedor, ni un país que no resuelve, ni un timeout dejan
 * el paywall sin precio. Se pinta el catálogo editorial —que es lo que se
 * pintaba antes de existir esta ruta— y se puede comprar igual; el checkout de
 * Dodo enseña el importe real antes de pagar.
 */
export function resolvePaywallPrice(params: {
  quote: QuotedPrice | undefined
  editorialPrice: number
  editorialCurrency: PriceCurrency
  applyWebDiscount: boolean
}): ResolvedPaywallPrice {
  if (params.quote) {
    return {
      source: 'provider',
      amount: params.quote.amount,
      currency: params.quote.currency,
      originalAmount: params.quote.originalAmount,
    }
  }

  const discounted = params.applyWebDiscount && isWebDiscountActive()
  return {
    source: 'catalog',
    amount: discounted ? getDiscountedPrice(params.editorialPrice) : params.editorialPrice,
    currency: params.editorialCurrency,
    originalAmount: discounted ? params.editorialPrice : null,
  }
}


/** Un pack determina conjuntamente la política, el importe y la divisa de respaldo. */
export function resolvePackagePaywallPrice(packageId: PricedPackageId, context: {
  policies: PaywallPricingPolicies
  showcaseCurrency: PriceCurrency
  quotes: QuotedPricesByPackage
}): ResolvedPaywallPrice {
  const policy = packageId === TRIAL_PACKAGE_ID ? context.policies.oneMinute : context.policies.packages
  const currency = policy.fallbackCurrency ?? context.showcaseCurrency
  return resolvePaywallPrice({
    quote: context.quotes[packageId],
    editorialPrice: getPrice(packageId, currency),
    editorialCurrency: currency,
    applyWebDiscount: policy.applyWebDiscount,
  })
}

/**
 * Cómo se pinta un precio ya resuelto.
 *
 * Las **dos** vías se formatean con `Intl` y la divisa real, nunca con un
 * símbolo de una tabla nuestra: es lo que pide ADR-021 («el formateo debe salir
 * de la divisa real») y lo único que funciona cuando el cargo llega en pesos,
 * yenes o rupias — el yen ni siquiera lleva decimales, y «¥947,00» sería
 * sencillamente falso. `locale` es el idioma que el padre eligió en la app, la
 * misma señal con la que se pinta el checkout (fase 7).
 *
 * El catálogo tenía su propio render histórico (`€4.99`) hasta que el producto
 * pasó de un mercado a siete: en francés y en italiano se escribe `4,99 €`, y el
 * paywall ya lo pintaba así en la misma sesión en la que la landing decía
 * `€4.99`. Ver `formatPrice`.
 */
export function paywallPriceDisplay(params: {
  price: ResolvedPaywallPrice
  locale: string
}): PriceDisplay {
  const { price } = params

  const format = (amount: number) => formatPrice(amount, price.currency, params.locale)

  return priceDisplayOf(price.amount, price.originalAmount, format)
}

/**
 * Precio del catálogo EDITORIAL para landings y modal de checkout, que siguen
 * siendo escaparate y quedan fuera de la enmienda.
 */
export function editorialPriceDisplay(params: {
  price: number
  currency: PriceCurrency
  locale: string
}): PriceDisplay {
  const format = (amount: number) => formatPrice(amount, params.currency, params.locale)

  return priceDisplayOf(
    getDiscountedPrice(params.price),
    isWebDiscountActive() ? params.price : null,
    format
  )
}

/**
 * Un solo formateador para las dos vías, con `Intl` y la divisa real.
 *
 * El catálogo tenía su propio render (`€4.99`: símbolo delante, punto decimal)
 * heredado de cuando el producto solo se vendía en español. Con siete mercados
 * eso se rompe: el francés y el italiano escriben `4,99 €` —coma decimal,
 * símbolo detrás— y el paywall de Dodo, que ya pasaba por `Intl`, lo pintaba
 * así en la MISMA sesión en la que la landing decía `€4.99`. Dos formatos para
 * un precio, y ninguno de los dos elegido por el mercado que lo lee.
 *
 * Esto NO cambia de dónde sale el precio: la regla de ADR-021 (el paywall pide
 * el precio al proveedor, el catálogo es el fallback) sigue intacta, y
 * `resolvePaywallPrice` es quien la aplica. Aquí solo se decide cómo se escribe
 * el número una vez resuelto.
 */
export function formatPrice(amount: number, currency: string, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount)
}

/**
 * **El único sitio donde se decide si hay rebaja**, y tiene delante los dos
 * números que se van a pintar.
 *
 * Un solo umbral cubre los tres modos de fallo, y por eso no son tres guardas:
 * si `originalAmount` no supera al importe —porque coinciden, o porque el
 * redondeo de la conversión lo dejó por debajo— el porcentaje sale ≤ 0; si la
 * rebaja existe pero no llega a redondear a un 1%, sale 0. En ambos casos no hay
 * nada que anunciar y se pinta un precio a secas.
 *
 * Hoy los dos importes llegan **positivos y finitos** —del catálogo, donde
 * `getPrice` descarta ≤ 0, o de `parseQuotedPrices`, que rechaza el resto—, así
 * que el porcentaje no puede salir `NaN`. Aun así se comprueba: esta función es
 * un helper puro y compartido, y su corrección no debería depender de lo que
 * garanticen tres llamantes en otros módulos. Sin la comprobación, un `NaN`
 * caería del lado de `sale` (`NaN < 1` es `false`) y pintaría un badge «-NaN%».
 */
function priceDisplayOf(
  amount: number,
  originalAmount: number | null,
  format: (amount: number) => string
): PriceDisplay {
  const price = format(amount)
  if (originalAmount === null) return { kind: 'plain', price }

  const percent = Math.round(((originalAmount - amount) / originalAmount) * 100)
  if (!Number.isFinite(percent) || percent < 1) return { kind: 'plain', price }

  return {
    kind: 'sale',
    price,
    originalPrice: format(originalAmount),
    discountLabel: `-${percent}%`,
  }
}
